/**
 * From the server's surface answer to the tab row (`KLQ-20260819-004` §2, §3).
 *
 * This module is the one place that knows two different things, and keeping them apart is
 * the whole job:
 *
 * 1. **What the caller may reach** — the server's, from `SURFACE_MATRIX`. A surface whose
 *    access is `none` produces no tab at all (invariant 2: not rendered, never disabled).
 * 2. **What this codebase has built** — ours, and `ROUTES` now holds all eight. A permitted
 *    surface with no route renders as a disabled tab, which is a different rendering with a
 *    different meaning; there is nothing in that state today.
 *
 * Each later wave adds one line to `ROUTES` and its tab lights up; nothing else here moves.
 * Tasks was the last hole and closed with `KLQ-20260821-001`.
 */

import type { ProjectTab, ProjectTabKey } from '@/components/chrome';

import type { ProjectSurfaces } from './api';

/**
 * Tab order is chat1.md §4, verbatim, and the surface each one asks about.
 *
 * TWIN: `Surface` in `backend/src/kalqa/core/authz.py`. A surface renamed there is a lookup
 * that silently returns `undefined` here — which this module reads as "no access" and hides
 * the tab, so the failure is a screen that vanishes rather than one that leaks. The backend
 * test `tests/domains/test_project_surfaces.py` holds the wire against the enum; this line
 * is the client half of that pair.
 */
const SURFACE_OF: Readonly<Record<ProjectTabKey, string>> = {
  overview: 'project_overview',
  drawings: 'drawings',
  register: 'register',
  transmittals: 'transmittals',
  reviews: 'reviews',
  tasks: 'tasks',
  activity: 'activity_log',
  people: 'people',
  settings: 'project_settings',
};

const ORDER: readonly ProjectTabKey[] = [
  'overview',
  'drawings',
  'register',
  'transmittals',
  'reviews',
  'tasks',
  'activity',
  'people',
  'settings',
];

/**
 * The tabs that exist as screens today. **Add a line when a wave ships one** — the tab row
 * and the index redirect both follow from this object, so a screen that lands without an
 * entry here is a screen nobody can navigate to.
 */
const ROUTES: Partial<Readonly<Record<ProjectTabKey, string>>> = {
  overview: '/projects/$projectId/overview',
  drawings: '/projects/$projectId/drawings',
  register: '/projects/$projectId/register',
  transmittals: '/projects/$projectId/transmittals',
  reviews: '/projects/$projectId/reviews',
  tasks: '/projects/$projectId/tasks',
  activity: '/projects/$projectId/activity',
  people: '/projects/$projectId/people',
  settings: '/projects/$projectId/settings',
};

/**
 * Whether this caller reaches this surface at all — the one predicate, asked in one place.
 *
 * **Absent is `none`, not "unknown".** `surfaces_for_role` publishes *every* surface, `none`
 * included, so a missing key means the two sides disagree about the enum — a rename, an older
 * client, a newer server. `?.access !== 'none'` alone reads that as *permitted*, because
 * `undefined !== 'none'`, which is the wrong direction for a tenant boundary to fail in.
 *
 * It lives here as a named function rather than inline in both callers because it was inline in
 * both callers, and they disagreed: `tabsFor` carried the absent-key guard and
 * `firstReachableTab` did not, so the index redirect could send somebody to a tab the row it
 * was about to draw would not contain. Latent — the server keeps its promise — and exactly the
 * kind of divergence that stops being latent the day something else changes.
 */
function reaches(surfaces: ProjectSurfaces, key: ProjectTabKey): boolean {
  const rule = surfaces.surfaces[SURFACE_OF[key]];
  return rule !== undefined && rule.access !== 'none';
}

/** What the tab row draws, and in what state, for this caller on this project. */
export function tabsFor(
  surfaces: ProjectSurfaces | undefined,
  active: ProjectTabKey | null,
): readonly ProjectTab[] {
  if (surfaces === undefined) return [];
  return ORDER.filter((key) => reaches(surfaces, key))
    .map((key) => {
      const to = ROUTES[key];
      return { key, active: key === active, ...(to === undefined ? {} : { to }) };
    });
}

/**
 * Where `/projects/{id}` sends somebody: the first tab that is both permitted and built.
 *
 * `null` when there is none — an engineer or a contractor in C0, who may reach the project
 * and none of the screens that exist yet. They get the shell's honest empty panel rather
 * than a redirect loop or a `403` (§3).
 */
export function firstReachableTab(surfaces: ProjectSurfaces | undefined): ProjectTabKey | null {
  if (surfaces === undefined) return null;
  return ORDER.find((key) => ROUTES[key] !== undefined && reaches(surfaces, key)) ?? null;
}

/** The route path for a tab, for the index redirect. */
export function routeOf(key: ProjectTabKey): string | undefined {
  return ROUTES[key];
}

/** `read` · `write` · `none` for one surface, as the screens ask it. */
export function accessTo(surfaces: ProjectSurfaces | undefined, key: ProjectTabKey): string {
  return surfaces?.surfaces[SURFACE_OF[key]]?.access ?? 'none';
}
